import { useState, useMemo, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { 
  calculateDuration, 
  calculateSubtaskProgress, 
  getTaskProgress, 
  getCompletionStatus,
  getDescriptionStats,
  generateId
} from '../utils';

export const useTaskDetails = (task) => {
  const [comments, setComments] = useState(task?.comments || []);
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [subtasks, setSubtasks] = useState(task?.subtasks || []);
  const [newSubtask, setNewSubtask] = useState('');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  useEffect(() => {
    if (task) {
      setComments(task.comments || []);
      setSubtasks(task.subtasks || []);
    }
  }, [task]);

  // Calculate task statistics
  const taskStats = useMemo(() => {
    if (!task) return {};
    
    const duration = calculateDuration(task.startDate, task.endDate);
    const daysRemaining = task.endDate ? dayjs(task.endDate).diff(dayjs(), 'day') : 0;
    const progress = getTaskProgress(task.column);
    
    // Calculate subtask progress
    const totalSubtasks = subtasks.length;
    const completedSubtasks = subtasks.filter(st => st.completed).length;
    const subtaskProgress = calculateSubtaskProgress(subtasks);
    
    // Calculate description stats
    const descriptionStats = getDescriptionStats(task.description);
    
    return {
      duration,
      daysRemaining,
      progress,
      subtaskProgress,
      completedSubtasks,
      totalSubtasks,
      ...descriptionStats,
      isOverdue: daysRemaining < 0,
      completionStatus: getCompletionStatus(task.column)
    };
  }, [task, subtasks]);

  // Comment handlers
  const addComment = () => {
    if (newComment.trim()) {
      const comment = { 
        id: generateId('comment-'),
        author: 'Current User', 
        text: newComment,
        timestamp: new Date().toISOString(),
        avatar: 'CU'
      };
      setComments(prev => [...prev, comment]);
      setNewComment('');
      message.success('Comment added successfully!');
    }
  };

  // Subtask handlers
  const addSubtask = () => {
    if (newSubtask.trim()) {
      const subtask = {
        id: generateId('subtask-'),
        content: newSubtask.trim(),
        completed: false,
        createdAt: new Date().toISOString()
      };
      setSubtasks(prev => [...prev, subtask]);
      setNewSubtask('');
      message.success('Subtask added successfully!');
    }
  };

  const deleteSubtask = (subtaskId) => {
    setSubtasks(prev => prev.filter(subtask => subtask.id !== subtaskId));
    message.success('Subtask deleted!');
  };

  const toggleSubtask = (subtaskId) => {
    setSubtasks(prev => prev.map(subtask => 
      subtask.id === subtaskId 
        ? { 
            ...subtask, 
            completed: !subtask.completed,
            completedAt: !subtask.completed ? new Date().toISOString() : null
          }
        : subtask
    ));
  };

  const editSubtask = (subtaskId, newContent) => {
    setSubtasks(prev => prev.map(subtask => 
      subtask.id === subtaskId 
        ? { ...subtask, content: newContent }
        : subtask
    ));
  };

  // Tab handlers
  const setTab = (tab) => {
    setActiveTab(tab);
  };

  // Description handlers
  const toggleDescriptionExpanded = () => {
    setDescriptionExpanded(prev => !prev);
  };

  const copyDescription = () => {
    if (task?.description) {
      navigator.clipboard.writeText(task.description);
      message.success('Description copied to clipboard!');
    }
  };

  return {
    // State
    comments,
    newComment,
    activeTab,
    subtasks,
    newSubtask,
    descriptionExpanded,
    taskStats,
    
    // Comment handlers
    addComment,
    setNewComment,
    
    // Subtask handlers
    addSubtask,
    deleteSubtask,
    toggleSubtask,
    editSubtask,
    setNewSubtask,
    
    // Tab handlers
    setTab,
    
    // Description handlers
    toggleDescriptionExpanded,
    copyDescription
  };
};
